export type Ratio = '9:16' | '1:1' | '4:5' | '16:9' | 'free';

export const RATIO_DIMS: Record<Ratio, { w: number; h: number }> = {
  '9:16': { w: 1080, h: 1920 },
  '1:1': { w: 1080, h: 1080 },
  '4:5': { w: 1080, h: 1350 },
  '16:9': { w: 1920, h: 1080 },
  free: { w: 1080, h: 1080 },
};

/** Returns w/h for a fixed ratio. `free` returns null (no aspect lock). */
export function aspectFor(ratio: Ratio): number | null {
  if (ratio === 'free') return null;
  const d = RATIO_DIMS[ratio];
  return d.w / d.h;
}

export type Background =
  | { kind: 'color'; color: string }
  | { kind: 'gradient'; from: string; to: string; angle: number }
  | { kind: 'image'; src: string; fit: 'cover' | 'contain' }
  | { kind: 'video'; src: string; fit: 'cover' | 'contain'; loop: boolean; muted: boolean };

export type AnimationType = 'fade' | 'slide' | 'pop' | 'typing' | 'strike';
export type SlideDirection = 'left' | 'right' | 'top' | 'bottom';

export interface Animation {
  type: AnimationType;
  duration: number; // ms
  from?: SlideDirection;
  to?: SlideDirection;
}

export interface Transform {
  x: number; // px in canvas-local coords (top-left origin)
  y: number;
  w: number;
  h: number;
  rotate: number; // deg
  opacity: number; // 0..1
}

export type Easing = 'linear' | 'ease-in' | 'ease-out' | 'ease-in-out';

export interface Keyframe {
  id: string;
  /** Project-time in ms (NOT relative to layer.start). */
  t: number;
  /** Partial Layer snapshot — only the fields the user wants to animate. */
  values: Record<string, unknown>;
  ease: Easing;
}

/** Pure-CSS visual effects applied to a layer's wrapping element. All
 *  fields optional; "default" values are spelled here so engine code can
 *  short-circuit when a layer has no overrides. */
export interface LayerEffects {
  /** filter: blur(Npx) */
  blur?: number;
  /** filter: brightness(N) — 1 = no change, 0 = black, 2 = double */
  brightness?: number;
  /** filter: contrast(N) — 1 = no change */
  contrast?: number;
  /** filter: saturate(N) — 1 = no change, 0 = grayscale */
  saturate?: number;
  /** filter: grayscale(N) — 0..1 */
  grayscale?: number;
  /** filter: sepia(N) — 0..1 */
  sepia?: number;
  /** filter: hue-rotate(Ndeg) */
  hueRotate?: number;
  /** filter: invert(N) — 0..1 */
  invert?: number;
  /** filter: drop-shadow(...) — non-rectangular layers (PNG, shapes, text)
   *  follow the alpha boundary, unlike box-shadow which hugs the bounding box. */
  dropShadow?: { x: number; y: number; blur: number; color: string };
  /** box-shadow on the wrapping element. Use for rectangular media. */
  shadow?: { x: number; y: number; blur: number; spread: number; color: string; inset?: boolean };
  /** Corner round in px on the inner element (so the image / video / shape
   *  surface gets clipped, not the bounding box). */
  borderRadius?: number;
  /** Solid/dashed/dotted outline on the inner surface. */
  border?: { width: number; color: string; style: 'solid' | 'dashed' | 'dotted' };
  /** Solid color washed over the layer surface (Photoshop "color overlay").
   *  Rendered as an absolutely-positioned div on top of the content with
   *  the chosen blend mode + opacity. */
  colorOverlay?: {
    color: string;
    opacity?: number;          // 0..1, default 1
    blendMode?: string;        // any mix-blend-mode value, default 'normal'
  };
  /** Linear / radial gradient washed over the layer surface (Photoshop
   *  "gradient overlay"). */
  gradientOverlay?: {
    kind: 'linear' | 'radial';
    angle?: number;            // deg, linear only — default 90
    stops: { color: string; pos: number }[]; // pos 0..1
    opacity?: number;
    blendMode?: string;
  };
  /** mix-blend-mode against the layers behind. */
  blendMode?:
    | 'normal' | 'multiply' | 'screen' | 'overlay' | 'darken' | 'lighten'
    | 'color-dodge' | 'color-burn' | 'hard-light' | 'soft-light'
    | 'difference' | 'exclusion' | 'hue' | 'saturation' | 'color' | 'luminosity';
}

export interface LayerBase {
  id: string;
  name: string;
  visible: boolean;
  /** Optional UI tint for the layer's row + timeline bar. Hex like "#ff5a5f". */
  color?: string;
  /** Optional group membership. Layers with the same groupId render in the
      timeline under one collapsible group row. */
  groupId?: string;
  start: number; // ms
  end: number; // ms
  transform: Transform;
  enter?: Animation;
  exit?: Animation;
  /** Pure-CSS visual effects (filter / shadow / radius / blend). */
  effects?: LayerEffects;
  /** Optional inline CSS appended to the layer's inner element. ActionScript-style escape hatch. */
  customCss?: string;
  /** Optional JS body run on every animation tick. Receives (t, layer, el). */
  customScript?: string;
  /** Sorted ascending by t. Each keyframe carries a partial snapshot of
      inspector-editable values that interpolate over time. */
  keyframes?: Keyframe[];
}

export interface TextLayer extends LayerBase {
  kind: 'text';
  text: string;
  fontFamily: string;
  fontWeight: number;
  fontSize: number;
  color: string;
  letterSpacing: number;
  align: 'left' | 'center' | 'right';
  lineHeight: number;
}

export interface ImageLayer extends LayerBase {
  kind: 'image';
  src: string;
  fit: 'cover' | 'contain';
}

export interface VideoLayer extends LayerBase {
  kind: 'video';
  src: string;
  fit: 'cover' | 'contain';
  muted: boolean;
  loop: boolean;
}

export type ShapeKind =
  | 'box'
  | 'line'
  | 'circle'
  | 'triangle'
  | 'star'
  | 'diamond'
  | 'arrow'
  | 'heart'
  | 'pentagon'
  | 'hexagon';

export interface ShapeLayer extends LayerBase {
  kind: 'shape';
  shape: ShapeKind;
  fill: string;
  stroke: string;
  strokeWidth: number;
}

export interface DrawingStroke {
  /** Flat array [x0, y0, x1, y1, …] in canvas-local coords. */
  points: number[];
  color: string;
  width: number;
  /** 'pen' or 'eraser' (eraser uses destination-out). */
  mode?: 'pen' | 'eraser';
}

export interface DrawingLayer extends LayerBase {
  kind: 'drawing';
  strokes: DrawingStroke[];
  /** Layer-wide brush settings. The brush popover edits these when this
      layer is selected. New strokes inherit them; existing strokes are
      re-rendered with the override applied at draw time. */
  brush?: { color?: string; width?: number; shape?: 'round' | 'butt' };
}

export type Layer = TextLayer | ImageLayer | VideoLayer | ShapeLayer | DrawingLayer;

/** Background music for the clip. */
export type AudioTrack =
  | YoutubeAudioTrack
  | FileAudioTrack
  | SoundCloudAudioTrack
  | SpotifyAudioTrack;

interface AudioTrackBase {
  /** Stable id so multiple tracks can coexist and be edited individually. */
  id: string;
  /** Where in the song to start playing, in ms. Ignored by Spotify (no scrub). */
  songStart: number;
  /** Where in the song to stop, in ms. */
  songEnd: number;
  /** 0..1 */
  volume: number;
  /** ms — fade volume up at the start. */
  fadeIn?: number;
  /** ms — fade volume down before the end. */
  fadeOut?: number;
  /** Optional human label (e.g. song title) shown in the editor. */
  label?: string;
}

export interface YoutubeAudioTrack extends AudioTrackBase {
  kind: 'youtube';
  /** 11-char YouTube video ID. */
  videoId: string;
}

export interface FileAudioTrack extends AudioTrackBase {
  kind: 'file';
  /** data URL or http(s) URL pointing at an audio file (mp3/m4a/wav/ogg/flac/aac). */
  src: string;
  /** Original filename for display. */
  name?: string;
}

export interface SoundCloudAudioTrack extends AudioTrackBase {
  kind: 'soundcloud';
  /** Full SoundCloud URL (https://soundcloud.com/<artist>/<track>). */
  url: string;
}

export interface SpotifyAudioTrack extends AudioTrackBase {
  kind: 'spotify';
  /** Spotify track URL or URI (spotify:track:..., https://open.spotify.com/track/...). */
  url: string;
}

export interface Project {
  /** Stable unique id used as the IndexedDB key. */
  id: string;
  version: 1;
  name: string;
  /** ms since epoch. */
  createdAt: number;
  /** ms since epoch. */
  updatedAt: number;
  canvas: {
    /** Preset ratio. Ignored when `customSize` is set. */
    ratio: Ratio;
    /** Override canvas dimensions in logical px. */
    customSize?: { w: number; h: number };
    safeRatio: Ratio | null;
    background: Background;
  };
  duration: number; // ms
  layers: Layer[];
  /** Layer groups. Each group lists members + a collapsed flag. */
  groups?: LayerGroup[];
  /** Multiple background tracks can play together; new uploads append here. */
  audioTracks?: AudioTrack[];
  /** Photoshop-style guide lines overlaid on the canvas. Persist with the
      project so reopening shows the same layout aids. Hidden in record mode. */
  guides?: Guide[];
  /** @deprecated single-track field kept only for migration. */
  audio?: AudioTrack;
  /** Project-level CSS appended to <head>. @deprecated use scripts[] */
  globalCss?: string;
  /** Project-level JS run once at load and on every animation tick.
      The body receives implicit globals: t (ms), project, callTool.
      @deprecated use scripts[] */
  globalScript?: string;
  /** Named CSS / JS chunks. Enabled chunks are concatenated and applied
      together (CSS into <head>, JS as one combined function body). */
  scripts?: ScriptChunk[];
}

export interface ScriptChunk {
  id: string;
  name: string;
  lang: 'css' | 'js';
  enabled: boolean;
  source: string;
}

export interface Guide {
  id: string;
  /** Vertical guides span the full canvas height; horizontal span the width. */
  orientation: 'vertical' | 'horizontal';
  /** Position in canvas-local px (x for vertical, y for horizontal). */
  position: number;
  /** Line thickness in px. Default 1. */
  width?: number;
  /** Line colour (any CSS colour string). Default #5cd0ff. */
  color?: string;
  /** Hidden guides still snap dragging layers but are not painted on canvas. */
  visible?: boolean;
}

export interface LayerGroup {
  id: string;
  name: string;
  /** Optional group color (used as the bar tint when collapsed). */
  color?: string;
  /** Whether the group's children are collapsed into a single overlapping row. */
  collapsed: boolean;
}

export interface ProjectMeta {
  id: string;
  name: string;
  createdAt: number;
  updatedAt: number;
}
